import React from 'react'
import { Link } from 'react-router-dom'
import type { LucideIcon } from 'lucide-react'
import { cn } from 'lib/utils'

interface DashboardCardProps {
	title: string
	icon: LucideIcon
	/** Where the title goes. The title is the card's only "view all" link. */
	to?: string
	/** For a card with no page of its own (Chats opens a popup). */
	onTitleClick?: () => void
	/** Right side of the header: a caption, a toggle. */
	headerRight?: React.ReactNode
	/** Sets the card's fixed height and its grid placement. */
	className?: string
	/** Extra classes for the scrolling body, e.g. bottom padding. */
	bodyClassName?: string
	children: React.ReactNode
	'data-testid'?: string
}

const titleClass =
	'flex items-center gap-2 text-gray-700 text-base font-semibold no-underline hover:scale-105 transition-transform duration-150'

/**
 * One home dashboard card (MYD-530).
 *
 * Fixed size whatever the content: the body scrolls inside the card, so a
 * long list never stretches its row and an empty card never collapses.
 *
 * The body keeps a small MARGIN below it, so a list never runs into the
 * card's bottom edge. Margin rather than padding: padding on a scroll
 * container only shows once the list is scrolled to its end, so an
 * overflowing list would still be clipped flush against the edge.
 *
 * There is no footer. Each card used to end in a "View all …" link, which only
 * repeated the title link above it.
 */
const DashboardCard = ({
	title,
	icon: Icon,
	to,
	onTitleClick,
	headerRight,
	className,
	bodyClassName,
	children,
	'data-testid': testId,
}: DashboardCardProps) => {
	const heading = (
		<>
			<Icon className="w-4 h-4" /> {title}
		</>
	)

	return (
		<section
			data-testid={testId}
			aria-label={title}
			className={cn(
				'flex flex-col overflow-hidden bg-white rounded-2xl border border-gray-200 shadow-md hover:shadow-lg transition-shadow duration-200',
				className
			)}
		>
			<header className="flex items-center justify-between gap-2 px-5 py-3 bg-highlight border-b border-gray-100 shrink-0">
				{to ? (
					<Link to={to} className={titleClass}>
						{heading}
					</Link>
				) : onTitleClick ? (
					<button type="button" onClick={onTitleClick} className={titleClass}>
						{heading}
					</button>
				) : (
					<span className={titleClass}>{heading}</span>
				)}
				{headerRight}
			</header>
			<div className={cn('flex-1 min-h-0 mb-1 overflow-y-auto', bodyClassName)}>{children}</div>
		</section>
	)
}

export default DashboardCard
